// magic_moments.ts — P3 amplification dispatcher.
//
// Spec: docs/contracts/aha/state_machines/magic_moment.yaml.
// Lazy-loads canvas-confetti so the prod bundle stays slim when the
// FYD_AHA_MAGIC_MOMENTS flag is OFF (rollup tree-shakes the dynamic import
// chunk per ADDENDUM correction 8).

type ConfettiFn = (options?: Record<string, unknown>) => void;

let confettiCache: ConfettiFn | null = null;

async function ensureConfetti(): Promise<ConfettiFn | null> {
  if (confettiCache) return confettiCache;
  try {
    const mod = await import('canvas-confetti');
    confettiCache = mod.default as ConfettiFn;
    return confettiCache;
  } catch {
    return null;
  }
}

export interface BurstOptions {
  reducedMotion?: boolean;
  particleCount?: number;
  spread?: number;
  /** Tailwind/css color hexes; default vermilion-700 */
  colors?: string[];
}

/**
 * Fire a confetti burst centered on the given DOM element. Skipped silently
 * when reducedMotion is true or canvas-confetti is unavailable.
 */
export async function fireConfettiBurst(
  targetEl: HTMLElement | null,
  opts: BurstOptions = {},
): Promise<void> {
  if (!targetEl) return;
  if (opts.reducedMotion) return;
  const confetti = await ensureConfetti();
  if (!confetti) return;
  const rect = targetEl.getBoundingClientRect();
  const w = window.innerWidth || 1;
  const h = window.innerHeight || 1;
  confetti({
    particleCount: opts.particleCount ?? 80,
    spread: opts.spread ?? 70,
    colors: opts.colors ?? ['#C03026'],
    scalar: 0.7,
    origin: {
      x: (rect.left + rect.width / 2) / w,
      y: (rect.top + rect.height / 2) / h,
    },
  });
}

export const MAGIC_MOMENT_IDS = [
  'iron_kill_first_seen',
  'first_council_lifetime',
  'report_ready_first_complete',
] as const;

export type MagicMomentId = (typeof MAGIC_MOMENT_IDS)[number];

/** Storage key prefix used by useFireOncePerUser for each moment. */
export function magicMomentStorageKey(id: MagicMomentId): string {
  return `fyd_magic_moment_${id}`;
}
